import React from 'react';
import { PageNav, PageFooter, CookieBanner } from '../components/layout/LegalLayout';
import { MapPin, Clock } from 'lucide-react';

const C = { amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A', amber500:'#8B5A0A', ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570', border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF' };
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const roles = [
  { title:'Senior Backend Engineer', team:'Engineering', location:'Lagos / Remote', type:'Full-time', desc:'Own the API, PostgreSQL schema, and full-text search infrastructure. Experience with Node.js, TypeScript, and PostgreSQL required.' },
  { title:'Product Designer', team:'Design', location:'Lagos / Remote', type:'Full-time', desc:'Design the future of Jötr — from onboarding flows to advanced editor interactions. Strong systems thinking and Figma proficiency required.' },
  { title:'Growth Marketer', team:'Growth', location:'Remote', type:'Full-time', desc:'Drive user acquisition for Jötr across Nigeria and West Africa. SEO, content, and community experience preferred.' },
];

const CareersPage: React.FC = () => (
  <>
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap');
      *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
      body{font-family:'DM Sans',sans-serif;background:${C.surface};color:${C.ink};}
      #root{display:flex;flex-direction:column;min-height:100vh;}
    `}</style>
    <PageNav/>
    <main style={{ flex:1 }}>
      {/* Hero */}
      <div style={{ background:`linear-gradient(135deg,#FFF4E0,#FFFDF8)`, borderBottom:`1px solid ${C.border}`, padding:'64px clamp(20px,5vw,60px)', textAlign:'center' }}>
        <div style={{ maxWidth:680, margin:'0 auto' }}>
          <p style={{ fontSize:12, fontWeight:700, color:C.amber400, textTransform:'uppercase', letterSpacing:'0.1em', fontFamily:font.body, marginBottom:14 }}>Join the team</p>
          <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(28px,5vw,48px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:16, lineHeight:1.1 }}>Help us build the note app Africa deserves</h1>
          <p style={{ fontSize:'clamp(14px,2vw,17px)', color:C.muted, fontFamily:font.body, lineHeight:1.7 }}>We're a small, ambitious team based in Lagos. Remote-friendly. Serious about quality.</p>
        </div>
      </div>

      <div style={{ maxWidth:860, margin:'0 auto', padding:'56px clamp(20px,5vw,60px) 80px' }}>
        {/* Perks */}
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:60 }}>
          {[
            ['🌍','Remote-first','Work from anywhere in Africa'],
            ['📈','Equity','Meaningful ownership from day one'],
            ['🎓','Learning budget','₦500k/year for courses and books'],
            ['🏥','Health cover','Full medical insurance for you and family'],
          ].map(([icon,title,desc]) => (
            <div key={title as string} style={{ padding:'22px 20px', background:C.white, borderRadius:14, border:`1px solid ${C.border}`, textAlign:'center' }}>
              <div style={{ fontSize:28, marginBottom:10 }}>{icon}</div>
              <p style={{ fontSize:14, fontWeight:700, color:C.ink, fontFamily:font.head, marginBottom:6 }}>{title as string}</p>
              <p style={{ fontSize:12, color:C.muted, fontFamily:font.body, lineHeight:1.5 }}>{desc as string}</p>
            </div>
          ))}
        </div>

        {/* Open roles */}
        <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(20px,3vw,28px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:24 }}>Open roles</h2>
        {roles.map(role => (
          <div key={role.title} style={{ background:C.white, border:`1px solid ${C.border}`, borderRadius:16, padding:'24px 28px', marginBottom:16, transition:'box-shadow .15s' }}
            onMouseEnter={e=>(e.currentTarget.style.boxShadow=`0 6px 24px rgba(0,0,0,0.07)`)}
            onMouseLeave={e=>(e.currentTarget.style.boxShadow='none')}>
            <div style={{ display:'flex', flexWrap:'wrap', justifyContent:'space-between', alignItems:'flex-start', gap:12 }}>
              <div style={{ flex:1, minWidth:220 }}>
                <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:6, flexWrap:'wrap' }}>
                  <span style={{ fontSize:11, fontWeight:700, padding:'2px 9px', borderRadius:20, background:C.amber100, color:C.amber500, fontFamily:font.body }}>{role.team}</span>
                  <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.muted, fontFamily:font.body }}><MapPin size={11}/>{role.location}</span>
                  <span style={{ display:'flex', alignItems:'center', gap:4, fontSize:12, color:C.muted, fontFamily:font.body }}><Clock size={11}/>{role.type}</span>
                </div>
                <h3 style={{ fontFamily:font.head, fontWeight:700, fontSize:'clamp(16px,2vw,19px)', color:C.ink, marginBottom:10 }}>{role.title}</h3>
                <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, lineHeight:1.65 }}>{role.desc}</p>
              </div>
              <a href={`mailto:careers@Jötr.co?subject=Application: ${role.title}`} style={{ display:'inline-flex', alignItems:'center', padding:'10px 20px', borderRadius:10, background:`linear-gradient(135deg,${C.amber300},${C.amber400})`, color:C.white, fontWeight:700, fontSize:14, textDecoration:'none', fontFamily:font.body, flexShrink:0, whiteSpace:'nowrap' }}>
                Apply now
              </a>
            </div>
          </div>
        ))}

        {/* No role */}
        <div style={{ marginTop:40, padding:'28px 32px', borderRadius:16, border:`1px solid ${C.border}`, background:C.surface, textAlign:'center' }}>
          <p style={{ fontSize:16, fontWeight:700, color:C.ink, fontFamily:font.head, marginBottom:8 }}>Don't see your role?</p>
          <p style={{ fontSize:14, color:C.muted, fontFamily:font.body, marginBottom:16, lineHeight:1.6 }}>We're always interested in exceptional people. Send a note and tell us how you'd make Jötr better.</p>
          <a href="mailto:careers@Jötr.co" style={{ display:'inline-flex', padding:'10px 22px', borderRadius:10, border:`1.5px solid ${C.border}`, color:C.inkLight, fontWeight:600, fontSize:14, textDecoration:'none', fontFamily:font.body }}>
            careers@Jötr.co
          </a>
        </div>
      </div>
    </main>
    <PageFooter/>
    <CookieBanner/>
  </>
);

export default CareersPage;
